import type { DefaultError, QueryClient, QueryKey } from '@tanstack/query-core'
import { useQuery } from '@tanstack/react-query'
import type { DefinedUseQueryResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import type { DefinedInitialDataOptions, UndefinedInitialDataOptions } from '@tanstack/react-query'

import { useSession } from './session'

export function useApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): UseQueryResult<TData, TError>

export function useApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError>

export function useApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): UseQueryResult<TData, TError>

export function useApiQuery(options: UseQueryOptions, queryClient?: QueryClient) {
  const { user, invalidate } = useSession()

  options.enabled = options.enabled === false ? false : !!user

  const qf = options.queryFn
  options.queryFn = async () => {
    try {
      return await (qf as any)()
    } catch (error) {
      if (error && (error as any).code === 401) {
        console.log('API ERROR', qf, error)
        invalidate()
      } else {
        console.error('API ERROR', qf, error)
      }
      throw error
    }
  }

  return useQuery(options, queryClient)
}
